/**
 * Hook for caching blunder detection and data snooping results
 *
 * Avoids re-running expensive detection when the user switches between
 * methods or parameters without changing the underlying point data.
 *
 * Entries belong to one input: the store's `inputRevision` together with the
 * identity of its `pointPairs` array. Store updates are immutable, so any
 * edit produces a new array (and edits that affect results also advance the
 * revision); a lookup for a different input discards the whole cache. This
 * replaces hashing every pair on every change.
 */

import { useMemo, useRef } from 'react';
import type {
  PointPair,
  SolverOperation,
  SolverOperationParams,
  SolverOperationResult,
} from '@/types';

export type DetectionOperation = Exclude<SolverOperation, 'solve'>;

/** The input a cached result was computed from. */
export interface DetectionInput {
  inputRevision: number;
  pointPairs: PointPair[];
}

type CacheEntries = {
  [K in DetectionOperation]: Map<string, SolverOperationResult<K>>;
};

const emptyEntries = (): CacheEntries => ({
  blunder: new Map(),
  blunderAll: new Map(),
  dataSnooping: new Map(),
});

/** Stable key for small, flat parameter objects. */
const paramsKey = (params: object): string =>
  Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join('&');

export interface BlunderCache {
  get: <K extends DetectionOperation>(
    operation: K,
    params: SolverOperationParams<K>,
    input: DetectionInput
  ) => SolverOperationResult<K> | undefined;
  set: <K extends DetectionOperation>(
    operation: K,
    params: SolverOperationParams<K>,
    input: DetectionInput,
    result: SolverOperationResult<K>
  ) => void;
  clear: () => void;
}

/** Returns a stable cache API (same object for the component's lifetime). */
export const useBlunderCache = (): BlunderCache => {
  const cacheRef = useRef<{
    input: DetectionInput | null;
    entries: CacheEntries;
  }>({ input: null, entries: emptyEntries() });

  return useMemo<BlunderCache>(() => {
    /** Entries for `input`, discarding everything cached for another input. */
    const entriesFor = (input: DetectionInput): CacheEntries => {
      const cache = cacheRef.current;
      if (
        cache.input?.inputRevision !== input.inputRevision ||
        cache.input.pointPairs !== input.pointPairs
      ) {
        cacheRef.current = { input, entries: emptyEntries() };
      }
      return cacheRef.current.entries;
    };
    return {
      get: (operation, params, input) =>
        entriesFor(input)[operation].get(paramsKey(params)),
      set: (operation, params, input, result) => {
        entriesFor(input)[operation].set(paramsKey(params), result);
      },
      clear: () => {
        cacheRef.current = { input: null, entries: emptyEntries() };
      },
    };
  }, []);
};
